import { type QueryClient } from '@tanstack/react-query';

import { type WorkspaceAgentThreadReopenedStatus } from '@air/analytics';
import { Agents } from '@air/api';
import { type AgentSessionResponse } from '@air/server-sdk';
import { callAirApi } from '@air/utils-api';

import { getPrivateWorkspaceAgentSessionGetKey } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionGet';

export const mapSessionStatusToThreadReopenedStatus = (
  sessionStatus: AgentSessionResponse['status'],
): WorkspaceAgentThreadReopenedStatus => (sessionStatus === 'archived' ? 'archived' : 'active');

export const toWorkspaceAgentThreadReopenedStatus = (
  sessionStatus: AgentSessionResponse['status'] | undefined,
): WorkspaceAgentThreadReopenedStatus | null => {
  if (sessionStatus !== undefined) {
    return mapSessionStatusToThreadReopenedStatus(sessionStatus);
  }

  return null;
};

export type ResolveWorkspaceAgentThreadReopenedStatusParams = {
  queryClient: QueryClient;
  sessions: readonly AgentSessionResponse[];
  sessionId: string;
  workspaceId: string;
};

export const resolveWorkspaceAgentThreadReopenedStatus = async ({
  queryClient,
  sessions,
  sessionId,
  workspaceId,
}: ResolveWorkspaceAgentThreadReopenedStatusParams): Promise<WorkspaceAgentThreadReopenedStatus> => {
  const statusFromList = toWorkspaceAgentThreadReopenedStatus(sessions.find((entry) => entry.id === sessionId)?.status);

  if (statusFromList !== null) {
    return statusFromList;
  }

  const queryKey = getPrivateWorkspaceAgentSessionGetKey({ sessionId, workspaceId });
  const cachedSession = queryClient.getQueryData<AgentSessionResponse>(queryKey);

  if (cachedSession) {
    return mapSessionStatusToThreadReopenedStatus(cachedSession.status);
  }

  const inFlightQuery = queryClient.getQueryCache().find({ queryKey });

  if (inFlightQuery?.state.fetchStatus === 'fetching') {
    await inFlightQuery.fetch();
    const session = queryClient.getQueryData<AgentSessionResponse>(queryKey);

    if (session) {
      return mapSessionStatusToThreadReopenedStatus(session.status);
    }

    throw new Error('Workspace agent session status unavailable after in-flight Sessions.get');
  }

  // Standalone GET — analytics must not write into the panel's Sessions.get cache on failure.
  const session = await callAirApi(() =>
    Agents.Sessions.get({
      agentType: 'workspace',
      sessionId,
      workspaceId,
    }),
  );

  return mapSessionStatusToThreadReopenedStatus(session.status);
};
